import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../axios";
import { isDarkColor } from "../utils/routeColor";
import "../styles/analysis.css";

const DAY_TYPE_LABEL = { weekday: "平日", sat: "土曜", holiday: "日祝" };

// 自動更新の入切。**端末ごとに覚える。**見ながら止めたい場面（画面を見せて
// 説明している、時刻を控えている）があるので、開くたびに戻ると煩わしい。
const AUTO_KEY = "buswatch.analysis.stops.autoRefresh";
const AUTO_INTERVAL = 60000;

function loadAuto() {
  // localStorage はプライベートモードや設定次第で読めない。既定は入
  try {
    return window.localStorage.getItem(AUTO_KEY) !== "off";
  } catch {
    return true;
  }
}

function saveAuto(on) {
  try {
    window.localStorage.setItem(AUTO_KEY, on ? "on" : "off");
  } catch {
    /* 覚えられなくても動きは変えない */
  }
}

/**
 * バス停から探す。
 *
 * ## 時刻表の代わりにはしない
 *
 * 並べるのは**車号が付いた便だけ**。にしてつの時刻表をそのまま並べ直す
 * 画面ではない。ここが出せるのは「BusWatch の観測から、この便に入って
 * いると分かっている車両はどれか」で、時刻はそれを言うための添え物。
 *
 * だから通る便の総数を必ず添える。10 本並んでいても、そのバス停を通る便が
 * 53 本あるなら**そう書く。**書かないと、出ていない便は走らないと読まれる。
 *
 * ## 事実と予測を分ける
 *
 * その便を目撃していれば「確定」、していないが乗番から分かるなら「予測」。
 * 予測は乗番の途中で車両が交代すれば外れる。同じ見た目にしない。
 */
export default function AnalysisStopsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stop = searchParams.get("stop") || "";

  const [stops, setStops] = useState([]);
  const [query, setQuery] = useState("");
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [auto, setAuto] = useState(loadAuto);

  useEffect(() => {
    axios
      .get("/analysis/stops")
      .then((res) => setStops(res.data.stops))
      .catch(() => setError("バス停の一覧を読み込めませんでした。"));
  }, []);

  const fetchBoard = useCallback(() => {
    if (!stop) {
      setBoard(null);
      return;
    }
    setLoading(true);
    axios
      .get(`/analysis/stops/departures?stop=${encodeURIComponent(stop)}`)
      .then((res) => {
        setBoard(res.data);
        setError(null);
      })
      .catch((e) => setError(e.response?.data?.message ?? "読み込みに失敗しました。"))
      .finally(() => setLoading(false));
  }, [stop]);

  useEffect(() => {
    fetchBoard();
    // 発車案内なので、開きっぱなしでも進んでいくようにする。
    // ただし止められること（下の「時点」の表示が動かなくなる）
    if (!stop || !auto) return undefined;
    const timer = setInterval(fetchBoard, AUTO_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchBoard, stop, auto]);

  const toggleAuto = useCallback(() => {
    setAuto((on) => {
      saveAuto(!on);
      return !on;
    });
  }, []);

  const matches = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    return stops.filter((s) => s.name.includes(q)).slice(0, 30);
  }, [stops, query]);

  return (
    <div className="anl-page">
      <div className="anl-header">
        <button className="anl-back" onClick={() => navigate("/analysis")}>
          運用解析へ戻る
        </button>
        <h2 className="anl-title">バス停から探す</h2>
        <p className="anl-subtitle">
          指定したバス停をこれから通る便の内、<strong>どの車両が入るか判明している便</strong>を並べます。
        </p>
      </div>

      <div className="anl-card">
        <div className="anl-card__header">バス停を選ぶ</div>
        <div className="anl-card__body">
          <div className="anl-search">
            <input
              className="anl-input"
              type="text"
              placeholder="例: 箱崎浜"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {query.trim() !== "" && (
            matches.length === 0 ? (
              <p className="anl-empty">
                その名前のバス停は見つかりません。<br />
                時刻表を取り込んでいるのは、いま解析している路線が通るバス停だけです。
              </p>
            ) : (
              <div className="anl-route-chips">
                {matches.map((s) => (
                  <button
                    key={s.name}
                    className={`anl-stop-chip${s.name === stop ? " anl-stop-chip--active" : ""}`}
                    onClick={() => setSearchParams({ stop: s.name })}
                    title={`${s.trip_count}便が通ります`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {error && <div className="anl-warning">{error}</div>}

      {stop !== "" && (
        <div className="anl-card">
          <div className="anl-card__header">
            {stop}
            <span className="anl-board__head">
              {board && (
                <span style={{ fontWeight: 400, color: "#6b7280" }}>
                  {DAY_TYPE_LABEL[board.day_type] ?? board.day_type}ダイヤ · {board.now?.slice(11)}時点
                </span>
              )}
              <button
                type="button"
                className={`anl-toggle${auto ? " anl-toggle--on" : ""}`}
                onClick={toggleAuto}
                aria-pressed={auto}
                title={auto
                  ? "1分おきに読み直しています。押すと止まります"
                  : "止まっています。押すと1分おきに読み直します"}
              >
                自動更新{auto ? "ON" : "OFF"}
              </button>
            </span>
          </div>
          <div className="anl-card__body">
            {loading && !board ? (
              <p className="anl-empty">読み込み中…</p>
            ) : !board ? null : (
              <Board board={board} navigate={navigate} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// これ以上潰すと読めない。下限に当たったら、そこで止める
export const DEST_MIN_SCALE = 0.55;

/**
 * 入る幅に対する横倍率。入るならそのまま、はみ出すぶんだけ潰す。
 *
 * **文字数では決めない。**端末の幅も行先番号の桁数も違うので、9 文字などと
 * 決め打ちすると、広い画面では余白が余っているのに潰すことになる。
 */
export function fitScale(natural, avail, min = DEST_MIN_SCALE) {
  if (!(natural > 0) || !(avail > 0) || natural <= avail) return 1;
  return Math.max(min, avail / natural);
}

/**
 * 行先。**長くても「…」で切らない。**
 *
 * 「雁の巣レクリエーションセンター」のように 15 文字あるものが実在する
 * （98 便）。切ると行先が別物に見えるので、横倍率を下げて全部見せる。
 *
 * 入る幅は実測する。transform はレイアウトの幅を変えないので、測るときだけ
 * 一度 transform を外して素の幅を取る。
 */
function Destination({ name }) {
  const boxRef = React.useRef(null);
  const innerRef = React.useRef(null);
  const [scale, setScale] = useState(1);

  React.useLayoutEffect(() => {
    const box = boxRef.current;
    const inner = innerRef.current;
    if (!box || !inner) return undefined;

    const fit = () => {
      // 潰す前の幅で測る。transform がかかったままだと素の幅が取れない
      const keep = inner.style.transform;
      inner.style.transform = "none";
      const natural = inner.scrollWidth;
      inner.style.transform = keep;
      setScale(fitScale(natural, box.clientWidth));
    };

    fit();
    // 画面の回転や折り返しで入る幅が変わる。**親も見る。**自分の箱だけだと、
    // 隣の行先番号の幅が決まる前に測った値のままになる
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    if (box.parentElement) ro.observe(box.parentElement);
    return () => ro.disconnect();
  }, [name]);

  return (
    <span className="anl-board__dest" title={name} ref={boxRef} style={{ "--dest-scale": scale }}>
      <span className="anl-board__dest-inner" ref={innerRef}>{name}</span>
    </span>
  );
}

function Board({ board, navigate }) {
  if (board.total === 0) {
    return (
      <p className="anl-empty">
        このバス停を、これから通る便はありません。<br />
        {DAY_TYPE_LABEL[board.day_type] ?? board.day_type}の終バスが通過した後です。
      </p>
    );
  }

  return (
    <>
      {/* **出していない便があることを必ず言う。** 並んだ数がすべてに見えると、
          出ていない便は走らないと読まれる */}
      <p className="anl-note">
        これから通る便 <strong>{board.total}本</strong>の内
        {/* 全部を見たときに「12本の内手前の12本を見て」と書くと読みにくい */}
        {board.scanned < board.total && <>、手前の{board.scanned}本を見て</>}
        <strong> {board.departures.length}本</strong>は車両が予測できています。
        残りは情報が不足しており、何が走るか不明です。
      </p>

      {board.departures.length === 0 ? (
        <p className="anl-empty">
          今はどの便も車両が判明していません。<br />
          この時間帯の目撃が貯まると出るようになります。
        </p>
      ) : (
        <ul className="anl-board">
          {board.departures.map((d) => (
            <li key={`${d.passing_time}-${d.bus_number}-${d.departure_time}`} className="anl-board__row">
              <span className="anl-board__time">{d.passing_time}</span>

              <span className="anl-board__where">
                {/* 行先番号と行先は**同じ行に置く。**折り返す箱に入れると、
                    行先は縮む前に次の行へ落ちてしまう（flex は wrap が先） */}
                <span className="anl-board__line">
                  <span
                    className="anl-route-badge"
                    style={{
                      background: d.route_color || "#6b7280",
                      color: isDarkColor(d.route_color) ? "#fff" : "#111827",
                    }}
                  >
                    {d.route_no || "無番"}
                  </span>
                  <Destination name={d.destination ?? ""} />
                </span>
                <span className="anl-board__origin">
                  {d.start}発{d.departure_time}
                  {d.seq > 1 && <> · {d.seq}/{d.stop_count}停目</>}
                </span>
              </span>

              {/* 車号の下に乗番。出どころ（確定/予測）は縦書きにして脇へ置く。
                  横一列に並べると、その左にある行先を押し潰す */}
              <span className="anl-board__bus">
                <span className="anl-board__bus-stack">
                  <button className="anl-link" onClick={() => navigate(`/analysis/buses/${d.bus_number}`)}>
                    {d.bus_number}
                  </button>
                  {/* 乗番へ飛べるようにする。id が無いときは表示だけ（乗番に
                      属さない便では duty_code も出ないが、念のため分ける） */}
                  {d.duty_code && (
                    d.duty_id ? (
                      <button
                        className="anl-badge anl-badge--code anl-badge--link"
                        onClick={() => navigate(`/analysis/duties/${d.duty_id}`)}
                        title={`乗番${d.duty_code}を開く`}
                      >
                        {d.duty_code}
                      </button>
                    ) : (
                      <span className="anl-badge anl-badge--code">{d.duty_code}</span>
                    )
                  )}
                </span>
                <span
                  className={`anl-badge anl-badge--vertical ${
                    d.source === "observed" ? "anl-badge--high" : "anl-badge--forecast"
                  }`}
                >
                  {d.source === "observed" ? "確定" : "予測"}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
